import React, { useState, useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Content } from '../../types';
import SimpleRichTextEditor from '../Common/SimpleRichTextEditor';
import './ContentCard.css';

interface ContentCardProps {
  content: Content;
  index: number;
  isFirst: boolean;
  isLast: boolean;
}

export const ContentCard: React.FC<ContentCardProps> = ({ 
  content, 
  index, 
  isFirst, 
  isLast 
}) => {
  const { contentTypes, updateContent, deleteContent, addContentAfter, reorderContent } = useApp();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(content.title);
  const [contentText, setContentText] = useState(content.content_text);
  const [isNameVisible, setIsNameVisible] = useState(content.is_name_visible !== 0 && content.is_name_visible !== false);
  const [contentTypeId, setContentTypeId] = useState(content.content_type_id);

  // Update local state when content prop changes
  useEffect(() => {
    setTitle(content.title);
    setContentText(content.content_text);
    setIsNameVisible(content.is_name_visible !== 0 && content.is_name_visible !== false);
    setContentTypeId(content.content_type_id);
  }, [content]);

  const handleSave = async () => {
    await updateContent(content.id, {
      title,
      content_text: contentText,
      is_name_visible: isNameVisible,
      content_type_id: contentTypeId,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTitle(content.title);
    setContentText(content.content_text);
    setIsNameVisible(content.is_name_visible !== 0 && content.is_name_visible !== false);
    setContentTypeId(content.content_type_id);
    setIsEditing(false);
  };

  const handleAddBelow = async () => {
    await addContentAfter(content.id, {
      title: 'New Content',
      content_text: '<p>Enter your content here...</p>'
    });
  };

  const handleMoveUp = async () => {
    await reorderContent(content.id, 'up');
  };

  const handleMoveDown = async () => {
    await reorderContent(content.id, 'down');
  };

  const handleDelete = async () => {
    await deleteContent(content.id);
  };

  // Function to render HTML content safely
  const createMarkup = (html: string) => {
    return { __html: html };
  };

  return (
    <div className="content-card">
      <div className="content-card-header">
        {isEditing ? (
          <select 
            className="content-type-select"
            value={contentTypeId}
            onChange={(e) => setContentTypeId(e.target.value)}
          >
            {contentTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        ) : (
          <div className="content-type-display">
            {contentTypes.find(t => t.id === content.content_type_id)?.name || 'Unknown'}
          </div>
        )}
        <div className="content-actions">
          <button 
            className="btn btn-danger btn-sm"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </div>
      
      <div className="content-card-body">
        {isEditing ? (
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <div className="form-group">
              <label>Title</label>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <input 
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  style={{ 
                    flex: '1',
                    minWidth: '50%',
                    opacity: isNameVisible ? 1 : 0.5 
                  }}
                />
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  fontSize: '14px', 
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}>
                  <input 
                    type="checkbox"
                    checked={isNameVisible}
                    onChange={(e) => setIsNameVisible(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Show title in PDF
                </label>
              </div>
            </div>
            <div className="form-group">
              <label>Content</label>
              <SimpleRichTextEditor
                value={contentText}
                onChange={setContentText}
                placeholder="Enter your content here..."
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                Save Changes
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div onClick={() => setIsEditing(true)} className="content-preview">
            <h3 style={{
              ...(content.is_name_visible === 0 || content.is_name_visible === false ? {
                textDecoration: 'line-through',
                opacity: 0.5,
                color: '#94a3b8'
              } : {})
            }}>
              {content.title}
            </h3>
            <div 
              className="content-html-preview"
              dangerouslySetInnerHTML={createMarkup(content.content_text)}
            />
            <small className="edit-hint">Click to edit</small>
          </div>
        )}
      </div>
      
      <div className="content-card-footer">
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            className="btn btn-secondary btn-sm"
            onClick={handleMoveUp}
            disabled={isFirst}
          >
            ↑ Up
          </button>
          <button 
            className="btn btn-secondary btn-sm"
            onClick={handleMoveDown}
            disabled={isLast}
          >
            ↓ Down
          </button>
        </div>
        
        <button 
          className="btn btn-success btn-sm"
          onClick={handleAddBelow}
        >
          + Add Below
        </button>
      </div>
    </div>
  );
};